import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { Assessment } from '@schooly/api';
import { useNotifications } from '@schooly/components/notifications';
import {
  EmptySchoolSvg,
  GridRowCell,
  GridRowItem,
  GridRowName,
  GridRowStyled,
  randomInt,
} from '@schooly/style';
import React, { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { useMarkbookForAssessmentPreviewStyling } from '../../Markbook/common/assessmentStyling';
import { AverageResults, AverageResultsWrapper } from '../../Markbook/common/AverageResults';
import {
  createGradeFinder,
  getAssessmentEntryColorFactory,
} from '../../Markbook/common/colouredCells';
import { MarkbookForGroup } from '../../Markbook/MarkbookForGroup';
import { MarkbookViewSettings } from '../../Markbook/MarkbookSettings/MarkbookSettings';
import { useAssessmentEntriesForGroup } from './hooks';

const SKELETON_ROWS_COUNT = 6;

interface AssessmentsPreviewEntriesTableProps {
  assessment: Assessment;
  groupId: string;
  settings: MarkbookViewSettings;
}

export const AssessmentsPreviewEntriesTable: FC<AssessmentsPreviewEntriesTableProps> = ({
  assessment,
  groupId,
  settings,
}) => {
  const styling = useMarkbookForAssessmentPreviewStyling();
  const { showError } = useNotifications();

  const {
    assessmentForGroup,
    group,
    isEntriesLoading,
    isGroupLoading,
    assessmentGrade,
    averageData,
    subjectAverageData,
    setBenchmarkSubjectAverage,
    isAverageLoading,
    handleSuccessfulEntryUpdate,
    isLoadingGrade,
    setBenchmarkEnabled,
    setBenchmarkAverage,
    benchmarkEnabled,
  } = useAssessmentEntriesForGroup({
    assessment,
    groupId,
    settings,
  });

  const { gradeList, gradeFinders } = useMemo(() => {
    const gradeList = assessmentGrade ? { [assessmentGrade.id]: assessmentGrade } : {};
    const finders = new Map<string, ReturnType<typeof createGradeFinder>>();

    if (!settings.avgResults && !settings.coloredCells) return { gradeList, gradeFinders: finders };

    Object.entries(gradeList).forEach(([selectListId, grade]) => {
      finders.set(selectListId, createGradeFinder(grade.options));
    });

    return { gradeList, gradeFinders: finders };
  }, [assessmentGrade, settings.avgResults, settings.coloredCells]);

  const getAssessmentEntryColor = settings.coloredCells
    ? getAssessmentEntryColorFactory(averageData, gradeList, gradeFinders)
    : undefined;

  const averageResultsColumns = useMemo(() => {
    if (!settings.avgResults) return [];

    return (
      assessmentForGroup?.methods.map((method) => ({
        type: 'assessment' as const,
        assessment: assessmentForGroup,
        methodId: method.id ?? '',
      })) ?? []
    );
  }, [assessmentForGroup, settings.avgResults]);

  if (isGroupLoading || isEntriesLoading) {
    return <AssessmentsPreviewEntriesTableSkeleton />;
  }

  if (!group || group.students.length <= 0 || !assessmentForGroup) {
    return <AssessmentsPreviewEntriesEmptyStub />;
  }

  return (
    <Stack sx={{ height: '100%', flex: 1, minHeight: 0 }}>
      <Stack sx={{ flex: 1, minHeight: 0 }}>
        <MarkbookForGroup
          groupId={groupId}
          assessments={[assessmentForGroup]}
          students={group.students}
          lists={gradeList}
          styling={styling}
          getAssessmentEntryColor={getAssessmentEntryColor}
          onUpdateError={showError}
          showPercentage={settings.scoreInPercentage}
          onSuccess={handleSuccessfulEntryUpdate}
        />
      </Stack>
      <AverageResultsWrapper modalView opened={settings.avgResults}>
        <AverageResults
          subjectAverageData={subjectAverageData}
          setBenchmarkSubjectAverage={setBenchmarkSubjectAverage}
          columns={averageResultsColumns}
          averageData={averageData}
          isLoading={isAverageLoading || isLoadingGrade}
          studentColumnWidth={styling.studentNameCellWidth}
          gradeFinders={gradeFinders}
          gradeList={gradeList}
          setBenchmarkEnabled={setBenchmarkEnabled}
          setBenchmarkAverage={setBenchmarkAverage}
          benchmarkEnabled={benchmarkEnabled}
          anchorOffset={0}
          showPercentage={settings.scoreInPercentage}
        />
      </AverageResultsWrapper>
    </Stack>
  );
};

export const AssessmentsPreviewEntriesTableSkeleton = () => {
  return (
    <Box>
      {[...new Array(SKELETON_ROWS_COUNT)].map((_, i) => {
        return (
          <GridRowStyled sx={{ pointerEvents: 'none', overflow: 'hidden' }} key={i}>
            <GridRowItem gap={1} sx={{ padding: 1.25 }}>
              <GridRowName minWidth={200} variant={'h3'}>
                <Skeleton width={`${randomInt(50, 100)}%`} />
              </GridRowName>
              <GridRowCell minWidth={100} variant={'h3'}>
                <Skeleton width={100} />
              </GridRowCell>
              <GridRowCell minWidth={100} variant={'h3'}>
                <Skeleton width={100} />
              </GridRowCell>
              <GridRowCell minWidth={100} variant={'h3'}>
                <Skeleton width={100} />
              </GridRowCell>
            </GridRowItem>
          </GridRowStyled>
        );
      })}
    </Box>
  );
};

const AssessmentsPreviewEntriesEmptyStub = () => {
  return (
    <Stack
      sx={{ flex: 1, alignItems: 'center', justifyContent: 'center', '& svg': { height: 180 } }}
    >
      <EmptySchoolSvg />
      <Typography>
        <FormattedMessage id="assessments-EmptyEntries" />
      </Typography>
    </Stack>
  );
};
